import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { glossary } from '@sorare/core/src/lib/glossary';

import { PodiumBar } from '../PodiumBar';
import { ColoredRanking } from '../RankRewardTitle/ColoredRanking';

const BrandSup = styled(BrandXXS).attrs({ as: 'sup', bold: true })``;
const Root = styled(Horizontal)`
  position: relative;
  width: 100%;
`;
const StyledButtonBase = styled(ButtonBase)`
  text-align: left;
`;
const StyledBezel = styled(Bezel)`
  --podium-color: var(--c-nd-100);

  position: relative;
  padding: var(--intermediate-unit);
  flex: 1;

  background: linear-gradient(
      199deg,
      var(--podium-color) -40%,
      var(--c-black) 20%
    ),
    var(--c-black);

  &.rank-1 {
    --podium-color: #fcff63;
  }
  &.rank-2 {
    --podium-color: #99bdc8;
  }
  &.rank-3 {
    --podium-color: #ffa463;
  }
  &.isMe {
    --podium-color: var(--c-white);
  }
  &.isSelected {
    --podium-color: var(--c-blue-400);
  }
`;
const Wrapper = styled(Horizontal)`
  & > :first-child {
    flex: 1;
  }
`;

type Props = {
  rank: number | null;
  isMe?: boolean;
  isSelected?: boolean;
  title?: ReactNode;
  children?: FC<React.PropsWithChildren<{ expanded: boolean }>>;
};
export const CumulativeRankRow = ({
  rank,
  isMe,
  isSelected,
  title,
  children,
}: Props) => {
  const { formatMessage } = useIntl();
  const [expanded, toggleExpanded] = useToggle(false);

  const content = (
    <Root>
      <PodiumBar rank={rank} />
      <StyledBezel
        className={classNames(`rank-${rank}`, { isMe, isSelected })}
        borderColor={
          isSelected
            ? 'var(--c-blue-400)'
            : 'linear-gradient(199deg, var(--podium-color), var(--c-nd-100) 20%), var(--c-nd-100)'
        }
      >
        <Vertical gap={expanded ? 2 : 0}>
          <Wrapper>
            <div>{title}</div>
            <Horizontal>
              {!!rank && (
                <ColoredRanking className={`rank-${rank}`}>
                  <Ordinal ordinal={rank} Sup={BrandSup} />
                </ColoredRanking>
              )}
              {!!children && (
                <FontAwesomeIcon
                  icon={faChevronDown}
                  height={16}
                  aria-label={formatMessage(
                    expanded ? glossary.showLess : glossary.showMore
                  )}
                />
              )}
            </Horizontal>
          </Wrapper>
          <Collapse open={expanded} gap={0}>
            {children?.({ expanded })}
          </Collapse>
        </Vertical>
      </StyledBezel>
    </Root>
  );

  if (children) {
    return (
      <StyledButtonBase onClick={toggleExpanded}>{content}</StyledButtonBase>
    );
  }
  return content;
};
